import GENESIS_TOOLS from '@toolproof-core/genesis/_data/timestamped-resources/tools';
import { TOOL_INVOCATION_BY_HANDLE } from '@toolproof-core/genesis/tool-invocation-registry';
import { ToolSchema } from '@toolproof-core/genesis/zod/tool';
import { WorkflowSchema } from '@toolproof-core/genesis/zod/workflow';
import type { Resource, Tool, ToolStepRoleAddress, TracePointDelta } from '@toolproof-core/genesis/_contract/_index';
import type { LocalInvocationDescriptor } from '@toolproof-core/genesis/_contract/tool-invocation';

import { generateIdentifier } from '@toolproof-core/lib/identifier-generation';
import { bindMaterializedResource, materializeIntrinsicToolStepInputs } from '@toolproof-core/lib/_pure/mutable-workflow-overlay';
import { createResourceFromDescriptor } from '@toolproof-core/lib/_pure/resource-creation';
import { cloneForStep, cloneWhileStep, createBranchStepFromCases } from '@toolproof-core/lib/_pure/step-creation';
import {
    mutableStepKeyToStepPath,
    mutableWorkflowToWorkflow,
    projectMutableWorkflowStateEntriesByStepKeyToCanonicalDelta,
    workflowToMutableWorkflow,
} from '@toolproof-core/lib/_pure/workflow-canonicalization';
import { applyBranchContinuation, prepareBranchContinuation } from '@toolproof-core/lib/_pure/workflow-execution/branch-continuation';
import { rebindBranchExit, rebindLoopExit } from '@toolproof-core/lib/_pure/workflow-execution/exit-rebinding';
import { endExecution, startExecution } from '@toolproof-core/lib/_pure/workflow-execution/lifecycle';
import { applyLoopContinuation, bindLoopContinuation, prepareLoopContinuation } from '@toolproof-core/lib/_pure/workflow-execution/loop-continuation';
import { resolveNodeExecutionContextAtCursor } from '@toolproof-core/lib/_pure/workflow-execution/node-context';
import { resolveExecutionRoute } from '@toolproof-core/lib/_pure/workflow-execution/routing';
import {
    bindToolOutput,
    completeToolExecution,
    describeToolOutput,
    resolveToolInputs,
    resolveToolTraceContext,
} from '@toolproof-core/lib/_pure/workflow-execution/tool-execution';
import { stampAndAppendTracePoint } from '@toolproof-core/lib/_pure/workflow-execution/trace-point';
import { extractToolStepsFromWorkflow } from '@toolproof-core/lib/_pure/workflow-extraction';
import { resolveWorkflowStateChain } from '@toolproof-core/lib/_pure/workflow-state-resolution';
import type { ExecutionLifecycleUpdate, NodeExecutionContext, ToolInputResolution, ToolOutputBinding } from '@toolproof-core/lib/_contract/workflow-execution';
import type { MutableToolStep, MutableToolStepKey, MutableToolStepRoleAddress } from '@toolproof-core/lib/_contract/workflow-overlay';

import { applyExecutionUpdate } from '@/workflowExecution/_pure/applyUpdate';
import { requirePrimitiveType, requireTool, validatePrimitiveProjection, validatePrimitiveWorkflow } from '@/workflowExecution/_pure/validation';
import type {
    PrimitiveExecutionSnapshot,
    PrimitiveExecutionState,
    PrimitiveResourceTypeHandle,
    PrimitiveWorkflowSession,
    PrimitiveWorkflowSessionOptions,
    RuntimeInputRequirement,
} from '@/workflowExecution/_contract/types';

const hostName = 'CosmosPrimitiveWorkflow';

export function createPrimitiveWorkflowSession(options: PrimitiveWorkflowSessionOptions): PrimitiveWorkflowSession {
    const maxTransitions = options.maxTransitions ?? 1_000;
    if (!Number.isSafeInteger(maxTransitions) || maxTransitions <= 0) {
        throw new Error('maxTransitions must be a positive safe integer.');
    }
    let initialWorkflow = structuredClone(options.workflow);
    WorkflowSchema.parse(initialWorkflow);
    let mutableWorkflow = workflowToMutableWorkflow(initialWorkflow, generateIdentifier);
    const invocations: Readonly<Record<string, LocalInvocationDescriptor | undefined>> = TOOL_INVOCATION_BY_HANDLE;
    const toolMap = new Map(GENESIS_TOOLS.map(entry => ToolSchema.parse(entry.projection) as Tool)
        .filter(tool => invocations[tool.handle]?.invocationKind === 'local')
        .map(tool => [tool.handle, tool]));
    validatePrimitiveWorkflow(mutableWorkflow, toolMap);
    for (const step of extractToolStepsFromWorkflow(mutableWorkflow)) {
        const tool = requireTool(toolMap, step);
        const missingIntrinsic = Object.entries(tool.intrinsicInputByRoleName ?? {})
            .filter(([name, projection]) => {
                const entry = mutableWorkflow.workflowState[step.stepKey]?.[name];
                if (!entry) return true;
                if (entry.workflowStateEntryKind !== 'materializedResource' || entry.projection !== projection) {
                    throw new Error(`Intrinsic input '${name}' of '${tool.handle}' cannot be overridden.`);
                }
                return false;
            });
        if (missingIntrinsic.length) {
            mutableWorkflow = { ...mutableWorkflow, workflowState: materializeIntrinsicToolStepInputs({
                workflowState: mutableWorkflow.workflowState, toolStep: step,
                tool: { ...tool, intrinsicInputByRoleName: Object.fromEntries(missingIntrinsic) },
                generateIdentifier,
            }) };
        }
    }
    initialWorkflow = mutableWorkflowToWorkflow(mutableWorkflow);
    const traceHandle = generateIdentifier('WorkflowTraceHandle');
    let state: PrimitiveExecutionState = {
        mutableWorkflow, toolMap, currentThreadIndex: 0, stepCounters: {}, iterationCounters: {},
        tracePoints: [], tracePointIndexCounters: {}, rootLoopStepKeyByCloneLoopStepKey: {},
        rootBranchStepKeyByRemainderBranchStepKey: {}, loopLineageEntryByKey: {},
        branchExitRebindGuardByThreadIndex: {}, threadEndMap: {}, startEmitted: false, endEmitted: false,
    };
    let completed = false;
    let requirement: RuntimeInputRequirement | null = null;
    let inputSource: MutableToolStepRoleAddress | null = null;
    let failure: Extract<PrimitiveExecutionSnapshot, { status: 'failed' }>['error'] | null = null;
    let transitions = 0;
    const pendingInputStepKeys = new Set<MutableToolStepKey>();

    function getSnapshot(): PrimitiveExecutionSnapshot {
        const common = {
            trace: { handle: traceHandle, workflowHandle: initialWorkflow.handle, tracePoints: state.tracePoints },
            transitions,
        };
        if (requirement) return structuredClone({ ...common, status: 'waiting', requirement });
        if (failure) return structuredClone({ ...common, status: 'failed', error: failure });
        return structuredClone({ ...common, status: completed ? 'completed' : 'ready' });
    }

    function apply(update: ExecutionLifecycleUpdate): void {
        let next = applyExecutionUpdate(state, update);
        if (update.tracePoints?.[0]?.tracePointKind === 'tick' && pendingInputStepKeys.size) {
            // Mutable replay omits suspensions; commit supplied inputs after any tick relocations.
            const [first, ...rest] = update.tracePoints;
            let inputDelta: NonNullable<TracePointDelta['workflowStateDelta']> = {};
            for (const key of pendingInputStepKeys) {
                const entries = next.mutableWorkflow.workflowState[key];
                if (!entries) throw new Error('Runtime input resource was not bound.');
                inputDelta = { ...inputDelta, ...projectMutableWorkflowStateEntriesByStepKeyToCanonicalDelta(
                    next.mutableWorkflow, key, entries,
                ) };
            }
            const workflowStateDelta = { ...inputDelta, ...first.delta.workflowStateDelta };
            const patched = { ...first, delta: { ...first.delta, workflowStateDelta } };
            next = { ...next, tracePoints: [...state.tracePoints, patched, ...rest] };
            pendingInputStepKeys.clear();
        }
        state = next;
    }

    function createResource(type: PrimitiveResourceTypeHandle, projection: unknown): Resource {
        validatePrimitiveProjection(type, projection);
        return createResourceFromDescriptor({ id: generateIdentifier('ResourceId'), resourceTypeHandle: type }, projection);
    }

    function canonicalAddress(address: MutableToolStepRoleAddress): ToolStepRoleAddress {
        return { stepPath: mutableStepKeyToStepPath(state.mutableWorkflow, address.stepKey), roleName: address.roleName };
    }

    function suspendForInput(step: MutableToolStep, inputs: Extract<ToolInputResolution, { status: 'externalInputPotential' }>): void {
        const threadIndex = state.currentThreadIndex;
        if (threadIndex === null) throw new Error('No active local execution thread.');
        const source = inputs.path.at(-1);
        if (!source) throw new Error('External input dependency has no source address.');
        const tool = requireTool(state.toolMap, step);
        const type = requirePrimitiveType(tool.roleSpec.inputRoleByName[inputs.roleName]?.resourceTypeHandle);
        const nextRequirement: RuntimeInputRequirement = {
            requestId: crypto.randomUUID(), target: canonicalAddress(inputs.path[0]), source: canonicalAddress(source),
            dependencyPath: inputs.path.map(canonicalAddress), resourceTypeHandle: type,
        };
        apply(stampAndAppendTracePoint(state, threadIndex, {
            tracePointKind: 'suspended', workflowHandle: initialWorkflow.handle,
            threadIndex, counterSnapshot: { stepCounterAfter: state.stepCounters[threadIndex] ?? 0 },
            ...resolveToolTraceContext(state.mutableWorkflow, step),
            stepKind: 'tool', delta: { workflowStateDelta: {}, suspendedData: {} },
        }));
        requirement = nextRequirement;
        inputSource = source;
    }

    function executeTool(context: NodeExecutionContext<'tool'>): void {
        const { step, tool } = context.stepResolution;
        const trace = resolveToolTraceContext(state.mutableWorkflow, step);
        const inputs = resolveToolInputs(state.mutableWorkflow, step);
        if (inputs.status === 'unresolved') {
            throw new Error(`Unresolved input '${step.toolHandle}:${inputs.roleName}': ${inputs.reason}.`);
        }
        if (inputs.status === 'externalInputPotential') {
            suspendForInput(step, inputs);
            return;
        }
        for (const name of step.roleBindingSpec.inputBindings) {
            const type = requirePrimitiveType(tool.roleSpec.inputRoleByName[name]?.resourceTypeHandle);
            const resolved = resolveWorkflowStateChain(state.mutableWorkflow.workflowState, { stepKey: step.stepKey, roleName: name });
            if (resolved.status !== 'materializedResource' || resolved.entry.resourceTypeHandle !== type) {
                throw new Error(`Input ResourceType mismatch at '${step.toolHandle}:${name}'.`);
            }
            validatePrimitiveProjection(type, inputs.payload[name]);
        }
        const descriptor = invocations[step.toolHandle];
        if (!descriptor || descriptor.invocationKind !== 'local' || descriptor.inputMode !== 'projection' || descriptor.outputMode !== 'projection') {
            throw new Error(`Tool '${step.toolHandle}' has no local projection invocation.`);
        }
        const outputs: unknown = descriptor.invoke(inputs.payload);
        if (!outputs || typeof outputs !== 'object' || Array.isArray(outputs)) {
            throw new Error(`Tool '${step.toolHandle}' returned invalid outputs.`);
        }
        let binding: ToolOutputBinding = { workflowState: state.mutableWorkflow.workflowState, materializedByRoleName: {} };
        for (const [name, projection] of Object.entries(outputs)) {
            const output = describeToolOutput(tool, name, projection);
            binding = bindToolOutput(binding, step, name, createResource(requirePrimitiveType(output.resourceTypeHandle), projection));
        }
        apply(completeToolExecution(state, context, trace, binding));
    }

    function advance(): void {
        const threadIndex = state.currentThreadIndex;
        if (threadIndex === null) throw new Error('No active local execution thread.');
        const route = resolveExecutionRoute(state, threadIndex);
        if (route.routeKind === 'missing_thread') throw new Error(`Missing execution thread ${threadIndex}.`);
        if (route.routeKind === 'skip') {
            apply({ stepCounters: { [threadIndex]: route.nextIndex } });
            return;
        }
        if (route.target === 'thread_end') {
            apply(endExecution(state, hostName));
            if (state.endEmitted) completed = true;
            else state = { ...state, currentThreadIndex: threadIndex + 1 };
            return;
        }
        const threadSteps = state.mutableWorkflow.stepsByThreadIndex[threadIndex];
        const cursor = { currentThreadIndex: threadIndex, currentStepCounter: state.stepCounters[threadIndex] ?? 0, threadSteps };
        if (route.target === 'branch_exit_rebind') {
            apply(rebindBranchExit(state, cursor, hostName));
            return;
        }
        if (route.target === 'loop_exit_rebind') {
            apply(rebindLoopExit(state, cursor, hostName));
            return;
        }
        if (route.target === 'end') throw new Error('Unsupported local execution target.');
        if (route.target === 'tool') {
            const context = resolveNodeExecutionContextAtCursor({ state, nodeName: hostName, stepKind: 'tool' });
            if (!context.ok) throw new Error(context.error.message);
            executeTool(context.value);
        } else if (route.target === 'branch') {
            const context = resolveNodeExecutionContextAtCursor({ state, nodeName: hostName, stepKind: 'branch' });
            if (!context.ok) throw new Error(context.error.message);
            const plan = prepareBranchContinuation(state, context.value, hostName);
            const remainder = plan.remainderCases.length
                ? createBranchStepFromCases(plan.remainderCases, generateIdentifier)
                : null;
            apply(applyBranchContinuation(state, plan, remainder));
        } else {
            const context = resolveNodeExecutionContextAtCursor({ state, nodeName: hostName, stepKind: route.target });
            if (!context.ok) throw new Error(context.error.message);
            const step = context.value.stepResolution.step;
            const when = step.case.when;
            const spec = when.whenInputRoleBindingSpec;
            // Resolve retained inputs before clones can inherit unfulfilled external markers.
            const requiredInputs = [
                {
                    toolStep: step.case.what,
                    roleNames: step.case.what.roleBindingSpec.inputBindings
                        .filter(name => name !== step.case.loopFeedbackBindingSpec.targetInputRoleName),
                },
                {
                    toolStep: when,
                    roleNames: [
                        ...spec?.staticInputRoleNames ?? [],
                        ...(step.stepKind === 'for' ? spec?.dynamicInputRoleNames ?? [] : []),
                    ],
                },
            ];
            for (const { toolStep, roleNames } of requiredInputs) {
                for (const roleName of roleNames) {
                    const inputs = resolveToolInputs(state.mutableWorkflow, {
                        ...toolStep, roleBindingSpec: { ...toolStep.roleBindingSpec, inputBindings: [roleName] },
                    });
                    if (inputs.status === 'externalInputPotential') {
                        suspendForInput(toolStep, inputs);
                        return;
                    }
                    if (toolStep !== when) continue;
                    const address = { stepKey: toolStep.stepKey, roleName };
                    const entry = state.mutableWorkflow.workflowState[toolStep.stepKey]?.[roleName];
                    const resolved = resolveWorkflowStateChain(state.mutableWorkflow.workflowState, address);
                    if (entry?.workflowStateEntryKind === 'internalInputPotential' && resolved.status === 'materializedResource') {
                        state = { ...state, mutableWorkflow: {
                            ...state.mutableWorkflow,
                            workflowState: bindMaterializedResource(state.mutableWorkflow.workflowState, address, resolved.entry),
                        } };
                        pendingInputStepKeys.add(toolStep.stepKey);
                    }
                }
            }
            const plan = prepareLoopContinuation(state, context.value, hostName);
            const clone = step.stepKind === 'for'
                ? cloneForStep(step, generateIdentifier)
                : cloneWhileStep(step, generateIdentifier);
            const bindings = bindLoopContinuation(state, plan, clone, hostName);
            const iteration = bindings.iteration
                ? createResource(requirePrimitiveType(bindings.iteration.resourceTypeHandle), bindings.iteration.projection) : null;
            apply(applyLoopContinuation(state, plan, clone, bindings, iteration));
        }
    }

    function run(): PrimitiveExecutionSnapshot {
        if (requirement || failure || completed) return getSnapshot();
        try {
            if (!state.startEmitted) apply(startExecution(state, initialWorkflow, state.mutableWorkflow, hostName));
            while (!requirement && !completed) {
                if (transitions >= maxTransitions) {
                    failure = { code: 'transition_limit', message: `Local execution exceeded its ${maxTransitions}-transition budget.` };
                    break;
                }
                transitions += 1;
                advance();
            }
        } catch (error: unknown) {
            failure = { code: 'execution_failed', message: error instanceof Error ? error.message : String(error) };
        }
        return getSnapshot();
    }

    function provideInput(input: { requestId: string; projection: unknown }): PrimitiveExecutionSnapshot {
        if (!requirement || !inputSource || input.requestId !== requirement.requestId) {
            throw new Error('No matching pending runtime input request.');
        }
        const resource = createResource(requirement.resourceTypeHandle, input.projection);
        const nextWorkflow = {
            ...state.mutableWorkflow,
            workflowState: bindMaterializedResource(state.mutableWorkflow.workflowState, inputSource, resource),
        };
        pendingInputStepKeys.add(inputSource.stepKey);
        state = { ...state, mutableWorkflow: nextWorkflow };
        requirement = null;
        inputSource = null;
        return run();
    }

    return { getSnapshot, run, provideInput };
}
